import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLanguage } from "@/context/LanguageContext";
import { ExternalLink, RefreshCw } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtCapturedAt = (iso, lang) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(lang === "ar" ? "ar-AE" : "en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Asia/Dubai",
    });
  } catch (e) {
    return iso;
  }
};

const fmtPrice = (n, lang) => {
  if (n == null) return "—";
  try {
    return new Intl.NumberFormat(lang === "ar" ? "ar-AE" : "en-AE").format(n);
  } catch {
    return n.toLocaleString();
  }
};

export const MarketPulse = () => {
  const { t, lang } = useLanguage();
  const m = t.market;
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ transactions: [], captured_at: null, source_url: "" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/market/transactions`);
        if (!cancelled) setData(res.data || { transactions: [] });
      } catch (e) {
        console.error("Failed to load market data", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const txs = data.transactions || [];

  return (
    <section
      id="market"
      data-testid="market-section"
      className="relative bg-[#0A0A0A] border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-12">
          <div className="max-w-2xl">
            <div className="eyebrow mb-6" data-testid="market-eyebrow">
              {m.eyebrow}
            </div>
            <h2
              data-testid="market-title"
              className="font-serif-display text-4xl sm:text-5xl md:text-6xl font-light text-[#F8F8F8] leading-tight"
            >
              {m.title}
            </h2>
            <p className="mt-6 text-[#A1A1AA] leading-relaxed max-w-xl">
              {m.subtitle}
            </p>
          </div>

          <div className="text-xs uppercase tracking-[0.22em] text-[#71717A] md:text-end">
            <div className="flex items-center gap-2 md:justify-end">
              {loading && <RefreshCw size={12} className="animate-spin text-[#D4AF37]" />}
              <span>{m.updated}</span>
            </div>
            <div className="mt-2 text-[#F8F8F8] normal-case tracking-normal text-sm">
              {fmtCapturedAt(data.captured_at, lang)}
            </div>
            {data.source_url && (
              <a
                data-testid="market-source-link"
                href={data.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-[#D4AF37] hover:text-[#E6C657] transition-colors normal-case tracking-normal text-sm"
              >
                <ExternalLink size={12} /> {m.view_source}
              </a>
            )}
          </div>
        </div>

        {/* Desktop table */}
        <div className="hidden md:block border border-white/5 bg-[#0F0F0F]">
          <table
            data-testid="market-table"
            className="w-full text-sm"
            dir={lang === "ar" ? "rtl" : "ltr"}
          >
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.22em] text-[#71717A] border-b border-white/5">
                <th className="text-start font-normal px-5 py-4">{m.headers.building}</th>
                <th className="text-start font-normal px-5 py-4">{m.headers.location}</th>
                <th className="text-start font-normal px-5 py-4">{m.headers.type}</th>
                <th className="text-start font-normal px-5 py-4">{m.headers.beds}</th>
                <th className="text-end font-normal px-5 py-4">{m.headers.size}</th>
                <th className="text-end font-normal px-5 py-4">{m.headers.psf}</th>
                <th className="text-end font-normal px-5 py-4">{m.headers.price}</th>
                <th className="text-end font-normal px-5 py-4">{m.headers.date}</th>
              </tr>
            </thead>
            <tbody>
              {txs.length === 0 && !loading && (
                <tr>
                  <td colSpan={8} className="px-5 py-12 text-center text-[#71717A]">
                    {m.empty}
                  </td>
                </tr>
              )}
              {txs.map((row, i) => (
                <tr
                  key={i}
                  data-testid={`market-row-${i}`}
                  className="border-b border-white/5 last:border-b-0 hover:bg-[#141414] transition-colors"
                >
                  <td className="px-5 py-5 text-[#F8F8F8] font-serif-display text-lg">
                    {row.building || "—"}
                  </td>
                  <td className="px-5 py-5 text-[#A1A1AA]">{row.location || "—"}</td>
                  <td className="px-5 py-5 text-[#A1A1AA]">{row.type || "—"}</td>
                  <td className="px-5 py-5 text-[#A1A1AA]">{row.bedrooms || "—"}</td>
                  <td className="px-5 py-5 text-end text-[#A1A1AA]">
                    {row.size_sqft ? row.size_sqft.toLocaleString() : "—"}
                  </td>
                  <td className="px-5 py-5 text-end text-[#A1A1AA]">
                    {row.price_per_sqft ? row.price_per_sqft.toLocaleString() : "—"}
                  </td>
                  <td className="px-5 py-5 text-end text-[#D4AF37] font-serif-display text-lg">
                    {fmtPrice(row.price_aed, lang)}
                  </td>
                  <td className="px-5 py-5 text-end text-[#A1A1AA] whitespace-nowrap">
                    {row.date || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden space-y-4">
          {txs.length === 0 && !loading && (
            <div className="text-center text-[#71717A] py-12 border border-white/5">
              {m.empty}
            </div>
          )}
          {txs.map((row, i) => (
            <div
              key={i}
              data-testid={`market-card-${i}`}
              className="border border-white/5 bg-[#0F0F0F] p-5"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-serif-display text-lg text-[#F8F8F8]">
                    {row.building || "—"}
                  </div>
                  <div className="text-sm text-[#A1A1AA] mt-1">
                    {row.location || "—"}
                  </div>
                </div>
                <div className="text-end shrink-0">
                  <div className="font-serif-display text-lg text-[#D4AF37]">
                    AED {fmtPrice(row.price_aed, lang)}
                  </div>
                  <div className="text-xs text-[#71717A] mt-1">
                    {row.date || "—"}
                  </div>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-3 text-xs">
                <Cell label={m.headers.type} value={row.type} />
                <Cell label={m.headers.beds} value={row.bedrooms} />
                <Cell label={m.headers.size} value={row.size_sqft ? row.size_sqft.toLocaleString() : "—"} />
              </div>
            </div>
          ))}
        </div>

        <p className="mt-8 text-xs text-[#71717A] max-w-2xl leading-relaxed">
          {m.source}: Dubai Land Department open data, captured daily from{" "}
          <a
            href={data.source_url || "https://www.propertyfinder.ae/en/transactions/buy/dubai"}
            className="text-[#D4AF37] hover:text-[#E6C657]"
            target="_blank"
            rel="noopener noreferrer"
          >
            propertyfinder.ae
          </a>
          .
        </p>
      </div>
    </section>
  );
};

const Cell = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">{label}</div>
    <div className="mt-1 text-[#F8F8F8]">{value || "—"}</div>
  </div>
);

export default MarketPulse;
